import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { formatTashkentDate, formatTashkentWeekday, t } from '../../i18n';
import type { PrayerTime, WeekDayStatus, WeekResponse } from '../../types';
import { AppShell } from '../shared/AppShell';
import { Modal } from '../shared/Modal';

const PRAYER_NAME_KEY: Record<PrayerTime, string> = {
  bomdod: 'prayer.bomdod',
  peshin: 'prayer.peshin',
  asr: 'prayer.asr',
  shom: 'prayer.shom',
  xufton: 'prayer.xufton',
};

type Status = 'done' | 'in_progress' | 'not_started' | 'skipped';

function todayMonday(): string {
  const now = new Date();
  const local = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Tashkent' }));
  const dayOfWeek = local.getDay() === 0 ? 6 : local.getDay() - 1;
  local.setDate(local.getDate() - dayOfWeek);
  return local.toLocaleDateString('en-CA', { timeZone: 'Asia/Tashkent' });
}

function shiftDate(iso: string, days: number): string {
  const parts = iso.split('-').map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

const MANDATORY: PrayerTime[] = ['bomdod', 'peshin', 'asr'];

function recSummary(day: WeekDayStatus): { status: Status; count: number } {
  const count = day.prayers.reduce((s, p) => s + p.count, 0);
  const mandDone = day.prayers.filter(
    (p) => MANDATORY.includes(p.prayer_time) && p.status === 'done',
  ).length;
  const anyActivity = count > 0 || day.prayers.some((p) => p.status === 'done' || p.status === 'skipped');
  const status: Status = mandDone === MANDATORY.length ? 'done' : anyActivity ? 'in_progress' : 'not_started';
  return { status, count };
}

type DayDetail = { childName: string; day: WeekDayStatus };

export function WeekView() {
  const navigate = useNavigate();
  const [start, setStart] = useState(() => todayMonday());
  const [data, setData] = useState<WeekResponse | null>(null);
  const [detail, setDetail] = useState<DayDetail | null>(null);

  useEffect(() => {
    setData(null);
    api.week(start).then(setData);
  }, [start]);

  const days = useMemo(() => data?.days ?? [], [data]);

  return (
    <AppShell>
      <main className="mx-auto flex min-h-dvh max-w-3xl flex-col gap-4 p-5">
        <header className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} aria-label={t('common.back')} className="text-ink-muted">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-[18px] font-medium">Hafta</h1>
        </header>

        <div className="flex items-center justify-between">
          <button
            onClick={() => setStart(shiftDate(start, -7))}
            className="flex items-center gap-1 rounded-md bg-surface-muted px-3 py-1.5 text-[13px] text-ink-muted active:scale-[0.98]"
          >
            <ChevronLeft size={14} />
            Avvalgi
          </button>
          <span className="text-[13px] text-ink-muted">
            {days[0] ? formatTashkentDate(days[0]) : '…'} — {days[6] ? formatTashkentDate(days[6]) : '…'}
          </span>
          <button
            onClick={() => setStart(shiftDate(start, 7))}
            className="flex items-center gap-1 rounded-md bg-surface-muted px-3 py-1.5 text-[13px] text-ink-muted active:scale-[0.98]"
          >
            Keyingi
            <ChevronRight size={14} />
          </button>
        </div>

        <Legend />

        {!data ? (
          <p className="text-center text-[13px] text-ink-muted">{t('common.loading')}</p>
        ) : (
          <div className="flex flex-col gap-5 overflow-x-auto">
            {data.children.map((c) => {
              const memActive = c.daily_status.some((d) => d.memorization !== null);
              const recTotal = c.daily_status.reduce((s, d) => s + recSummary(d).count, 0);
              const memTotal = c.daily_status.reduce((s, d) => s + (d.memorization?.count ?? 0), 0);
              const cols = 'grid grid-cols-[96px_repeat(7,minmax(0,1fr))_48px]';
              return (
                <div key={c.id} className={`ramp-${c.color_ramp} flex min-w-[400px] flex-col gap-2`}>
                  <div className="text-[14px] font-medium">{c.name}</div>
                  <div className="overflow-hidden rounded-md border-hairline border-border bg-surface">
                    {/* header */}
                    <div className={`${cols} border-b-hairline border-border bg-surface-muted`}>
                      <span className="px-2 py-1.5" />
                      {c.daily_status.map((d) => (
                        <div key={d.quran_day} className="px-1 py-1.5 text-center">
                          <div className="text-[10px] uppercase tracking-wide text-ink-subtle">
                            {formatTashkentWeekday(d.quran_day)}
                          </div>
                          <div className="text-[11px] tabular-nums text-ink-muted">{d.quran_day.slice(8)}</div>
                        </div>
                      ))}
                      <div className="border-l-hairline border-border px-1 py-1.5 text-center text-[10px] uppercase tracking-wide text-ink-subtle">
                        Jami
                      </div>
                    </div>

                    {/* Takrorlash row */}
                    <div className={`${cols} border-b-hairline border-border ${memActive ? '' : 'last:border-b-0'}`}>
                      <span className="px-2 py-2 text-[12px] text-ink-muted">{t('module.recitation')}</span>
                      {c.daily_status.map((d) => {
                        const s = recSummary(d);
                        return (
                          <Cell
                            key={d.quran_day}
                            status={s.status}
                            count={s.count}
                            isRest={d.is_rest_day}
                            onClick={() => setDetail({ childName: c.name, day: d })}
                          />
                        );
                      })}
                      <div className="flex items-center justify-center border-l-hairline border-border bg-surface-muted/50 py-2 text-[12px] tabular-nums text-ink-muted">
                        {recTotal}
                      </div>
                    </div>

                    {/* Yodlash row */}
                    {memActive ? (
                      <div className={`${cols} last:border-b-0`}>
                        <span className="px-2 py-2 text-[12px] text-ink-muted">{t('module.memorization')}</span>
                        {c.daily_status.map((d) => (
                          <Cell
                            key={d.quran_day}
                            status={d.memorization?.status ?? 'not_started'}
                            count={d.memorization?.count ?? 0}
                            isRest={d.is_rest_day}
                            onClick={() => setDetail({ childName: c.name, day: d })}
                          />
                        ))}
                        <div className="flex items-center justify-center border-l-hairline border-border bg-surface-muted/50 py-2 text-[12px] tabular-nums text-ink-muted">
                          {memTotal}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <DayDetailModal detail={detail} onClose={() => setDetail(null)} />
    </AppShell>
  );
}

function Cell({
  status,
  count,
  isRest,
  onClick,
}: {
  status: Status;
  count: number;
  isRest: boolean;
  onClick: () => void;
}) {
  let bg = 'bg-transparent';
  let text = 'text-ink-muted';
  if (isRest) {
    bg =
      'bg-child-fill [background-image:repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(0,0,0,0.04)_4px,rgba(0,0,0,0.04)_8px)]';
  } else if (status === 'done') {
    bg = 'bg-child-accent/85';
    text = 'text-ink-inverse';
  } else if (status === 'in_progress') {
    bg = 'bg-child-fill';
    text = 'text-child-text';
  } else if (status === 'skipped') {
    bg = 'bg-ink/10';
    text = 'text-ink-subtle';
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-center border-l-hairline border-border py-2 text-[11px] tabular-nums active:scale-[0.97] ${text} ${bg}`}
    >
      {count > 0 ? count : ''}
    </button>
  );
}

function statusLabel(status: Status): string {
  if (status === 'done') return t('week.detail.done');
  if (status === 'in_progress') return t('week.detail.in_progress');
  if (status === 'skipped') return t('dashboard.skipped');
  return '—';
}

function DayDetailModal({ detail, onClose }: { detail: DayDetail | null; onClose: () => void }) {
  if (!detail) return null;
  const { childName, day } = detail;
  return (
    <Modal open onClose={onClose} title={`${childName} · ${formatTashkentDate(day.quran_day)}`}>
      <div className="flex flex-col gap-4">
        <section className="flex flex-col gap-1.5">
          <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
            {t('module.recitation')}
          </div>
          <ul className="flex flex-col">
            {day.prayers.map((p) => (
              <li key={p.prayer_time} className="flex items-center justify-between py-1 text-[13px]">
                <span>{t(PRAYER_NAME_KEY[p.prayer_time])}</span>
                <span className="text-ink-muted">
                  {statusLabel(p.status as Status)}
                  {p.count > 0 ? ` · ${t('dashboard.records', { n: p.count })}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
        {day.memorization ? (
          <section className="flex flex-col gap-1.5">
            <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
              {t('module.memorization')}
            </div>
            <div className="flex items-center justify-between py-1 text-[13px]">
              <span>{statusLabel(day.memorization.status)}</span>
              <span className="tabular-nums text-ink-muted">
                {day.memorization.count}/{day.memorization.total_target}
              </span>
            </div>
          </section>
        ) : null}
      </div>
    </Modal>
  );
}

function Legend() {
  const stripe =
    '[background-image:repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(0,0,0,0.04)_4px,rgba(0,0,0,0.04)_8px)]';
  const items: Array<{ swatch: string; title: string; desc: string }> = [
    { swatch: 'bg-success', title: "To'q rang", desc: 'kun maqsadi bajarildi' },
    { swatch: 'bg-success-bg', title: 'Xira rang', desc: 'qisman bajarildi' },
    { swatch: `bg-success-bg ${stripe}`, title: 'Chiziqli', desc: 'dam olish kuni' },
    { swatch: 'bg-transparent border-hairline border-border', title: "Bo'sh", desc: 'hech narsa qilinmagan' },
  ];
  return (
    <details className="group rounded-md border-hairline border-border bg-surface">
      <summary className="flex cursor-pointer select-none items-center justify-between px-4 py-2.5 text-[13px] text-ink-muted">
        <span>Belgilar nima ma'noda?</span>
        <span className="transition-transform group-open:rotate-90">›</span>
      </summary>
      <ul className="flex flex-col gap-2 border-t-hairline border-border px-4 py-3 text-[13px] text-ink-muted">
        {items.map((it) => (
          <li key={it.title} className="flex items-center gap-3">
            <span className={`inline-block h-4 w-6 rounded-sm ${it.swatch}`} />
            <span>
              <span className="font-medium text-ink">{it.title}</span> — {it.desc}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}
